import { supabase } from '@/lib/supabase';
import { logUserAudit } from '@/lib/userAudit';

// ─────────────────────────────────────────────────────────────
// ZIFEK — Activité d'un utilisateur (console SuperAdmin)
// Agrège le profil, le type de compte, les commandes (comme
// vendeur et comme client), les annonces, les applications,
// les rattachements marketplace et les notifications.
// ─────────────────────────────────────────────────────────────

export interface UserProfile {
  id: number;
  name: string;
  userName: string;
  email: string;
  telephone: string;
  type: string;
  typecompte: number;
  active: number;
  nomcommerce: string;
  description: string;
  image: string;
  ville: string;
  pays: string;
  quartier: string;
  adresse: string;
  monaie: string;
  langue: string;
  package: number;
  solde: number;
  datecreation: string | null;
  aboutus: string;
  googlemaps: string;
  lienfacebook: string;
  lieninstagram: string;
  lientiktok: string;
  presencemarketplace: string;
  tva: string;
}

export interface AccountTypeOption {
  id: number;
  nom: string;
  statustypecompte: number;
  actif: boolean;
}

export interface UserCommandeRow {
  id: number;
  titre: string;
  nomclient: string;
  totalttc: string;
  statut: string;
  dateTime: string | null;
  ville: string;
  role: 'vendeur' | 'client';
}

export interface UserAnnonceRow {
  id: number;
  titre: string;
  type: string;
  prix: string;
  ville: string;
  publique: number;
  isBoost: boolean;
  created: string | null;
}

export interface UserMarketRelation {
  id: number;
  marketplaceId: number;
  marketplaceName: string;
  validation: string;
  statut: number | null;
  misEnAvant: boolean;
  date: string | null;
}

export interface UserAppRow {
  id: number;
  nomapp: string;
  status: number;
  prix: number;
  nompage: string;
}

export interface UserNotificationRow {
  id: number;
  message: string;
  type: string;
  isRead: number;
  createdAt: string | null;
}

function str(value: unknown): string {
  return value == null ? '' : String(value);
}

function num(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Profil complet d'un utilisateur. */
export async function fetchUserProfile(id: number): Promise<UserProfile | null> {
  if (!Number.isFinite(id) || id <= 0) return null;
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id, name, user_name, email, telephone, type, typecompte, active, nomcommerce, description, image, Ville, Pays, Quartier, adresse, monaie, langue, package, solde, datecreation, aboutus, googlemaps, lienfacebook, lieninstagram, lientiktok, presencemarketplace, tva')
      .eq('id', id)
      .maybeSingle();
    if (error || !data) return null;
    const r = data as Record<string, unknown>;
    return {
      id: num(r.id),
      name: str(r.name),
      userName: str(r.user_name),
      email: str(r.email),
      telephone: str(r.telephone),
      type: str(r.type),
      typecompte: num(r.typecompte),
      active: num(r.active),
      nomcommerce: str(r.nomcommerce),
      description: str(r.description),
      image: str(r.image),
      ville: str(r.Ville),
      pays: str(r.Pays),
      quartier: str(r.Quartier),
      adresse: str(r.adresse),
      monaie: str(r.monaie),
      langue: str(r.langue),
      package: num(r.package),
      solde: num(r.solde),
      datecreation: r.datecreation ? String(r.datecreation) : null,
      aboutus: str(r.aboutus),
      googlemaps: str(r.googlemaps),
      lienfacebook: str(r.lienfacebook),
      lieninstagram: str(r.lieninstagram),
      lientiktok: str(r.lientiktok),
      presencemarketplace: str(r.presencemarketplace),
      tva: str(r.tva),
    };
  } catch {
    return null;
  }
}

/** Liste des types de compte disponibles (table typecompte). */
export async function fetchAccountTypeOptions(): Promise<AccountTypeOption[]> {
  try {
    const { data, error } = await supabase
      .from('typecompte')
      .select('id, nom, statustypecompte, actif')
      .order('id', { ascending: true });
    if (error || !data) return [];
    return (data as Record<string, unknown>[]).map((r) => ({
      id: num(r.id),
      nom: str(r.nom),
      statustypecompte: num(r.statustypecompte),
      actif: r.actif !== false,
    }));
  } catch {
    return [];
  }
}

/** Change le type de compte d'un utilisateur (typecompte + libellé). */
export async function updateUserAccountType(
  userId: number,
  typeId: number,
  typeLabel: string,
  previousLabel?: string,
): Promise<void> {
  const { error } = await supabase
    .from('users')
    .update({ typecompte: typeId, type: typeLabel })
    .eq('id', userId);
  if (error) throw error;
  // 🔎 Journal d'activité : changement de type de compte.
  void logUserAudit({
    userId,
    category: 'type_change',
    action: 'account_type_changed',
    description: previousLabel && previousLabel !== typeLabel
      ? `Type de compte : ${previousLabel} → ${typeLabel}`
      : `Type de compte défini sur ${typeLabel}`,
    metadata: { from: previousLabel ?? null, to: typeLabel, typecompte: typeId },
  });
}

/** Commandes où l'utilisateur est vendeur OU client. */
export async function fetchUserCommandes(userId: number): Promise<UserCommandeRow[]> {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from('commande')
      .select('id, titre, idvendeur, user_id, nomclient, totalttc, statut_com_vendeur, date_time, ville')
      .or(`idvendeur.eq.${userId},user_id.eq.${userId}`)
      .order('date_time', { ascending: false })
      .limit(100);
    if (error || !data) return [];
    return (data as Record<string, unknown>[]).map((r) => ({
      id: num(r.id),
      titre: str(r.titre),
      nomclient: str(r.nomclient),
      totalttc: str(r.totalttc),
      statut: str(r.statut_com_vendeur) || 'En attente',
      dateTime: r.date_time ? String(r.date_time) : null,
      ville: str(r.ville),
      role: num(r.idvendeur) === userId ? 'vendeur' : 'client',
    }));
  } catch {
    return [];
  }
}

/** Annonces / produits / services publiés par l'utilisateur. */
export async function fetchUserAnnonces(userId: number): Promise<UserAnnonceRow[]> {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from('annonce')
      .select('id, titre, type, prix, ville, publique, is_boost, created')
      .eq('idvendeur', userId)
      .order('created', { ascending: false })
      .limit(100);
    if (error || !data) return [];
    return (data as Record<string, unknown>[]).map((r) => ({
      id: num(r.id),
      titre: str(r.titre),
      type: str(r.type),
      prix: str(r.prix),
      ville: str(r.ville),
      publique: num(r.publique),
      isBoost: Boolean(r.is_boost),
      created: r.created ? String(r.created) : null,
    }));
  } catch {
    return [];
  }
}

/** Notifications reçues par l'utilisateur. */
export async function fetchUserNotifications(userId: number): Promise<UserNotificationRow[]> {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from('notifications')
      .select('id, message, type, is_read, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(50);
    if (error || !data) return [];
    return (data as Record<string, unknown>[]).map((r) => ({
      id: num(r.id),
      message: str(r.message),
      type: str(r.type),
      isRead: num(r.is_read),
      createdAt: r.created_at ? String(r.created_at) : null,
    }));
  } catch {
    return [];
  }
}

/** Applications installées pour cet utilisateur (appvendeur). */
export async function fetchUserApps(userId: number): Promise<UserAppRow[]> {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from('appvendeur')
      .select('id, nomapp, status, prix, nompage')
      .eq('idcommerce', userId)
      .order('id', { ascending: false });
    if (error || !data) return [];
    return (data as Record<string, unknown>[]).map((r) => ({
      id: num(r.id),
      nomapp: str(r.nomapp),
      status: num(r.status),
      prix: num(r.prix),
      nompage: str(r.nompage),
    }));
  } catch {
    return [];
  }
}

/** Rattachements marketplace de l'utilisateur (vendeur). */
export async function fetchUserMarketRelations(userId: number): Promise<UserMarketRelation[]> {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from('userwebsite')
      .select('id, idcommerce, validation, statut, mis_en_avant, date')
      .eq('iduser', userId);
    if (error || !data) return [];
    const rows = data as Record<string, unknown>[];
    const ids = rows.map((r) => num(r.idcommerce)).filter((n) => n > 0);
    const names = new Map<number, string>();
    if (ids.length > 0) {
      const { data: us } = await supabase
        .from('users')
        .select('id, nomcommerce, name')
        .in('id', ids);
      (us as Record<string, unknown>[] | null)?.forEach((u) => {
        names.set(num(u.id), str(u.nomcommerce) || str(u.name) || `#${u.id}`);
      });
    }
    return rows.map((r) => ({
      id: num(r.id),
      marketplaceId: num(r.idcommerce),
      marketplaceName: names.get(num(r.idcommerce)) || `Marketplace #${r.idcommerce}`,
      validation: str(r.validation) || 'en_attente',
      statut: r.statut == null ? null : num(r.statut),
      misEnAvant: Boolean(r.mis_en_avant),
      date: r.date ? String(r.date) : null,
    }));
  } catch {
    return [];
  }
}